/**
 * Client-side mirror of the backend's DeliveryCostCalculator -- used ONLY
 * to show a live estimate as the guest drags/taps the delivery pin, before
 * submitting. The real, charged DeliveryCost is always recomputed server-
 * side in BookingController.CreateBooking from the space's own current
 * pricing/location (never trusted from the client) -- this never needs to
 * match to the cent, just be close enough to not surprise the guest at
 * payment. Same straight-line-distance MVP approximation as the backend;
 * see that calculator's own doc comment for the real-routing-API note.
 */
export interface IDeliveryCostBreakdown {
  distanceKm: number;
  pricePerKm: number;
  fixedDispatchFee?: number;
  /** 1 (drop-off only) or 2 (drop-off + return pickup) -- see DeliveryCostCalculator.DeliveryCostBreakdown's own doc comment: each leg is its own round trip. */
  legs: number;
  total: number;
}

export const estimateDeliveryCost = (
  machineryLatitude: number,
  machineryLongitude: number,
  deliveryLatitude: number,
  deliveryLongitude: number,
  pricePerKmDelivery: number,
  fixedDispatchFee?: number,
  returnPickupRequested?: boolean
): IDeliveryCostBreakdown => {
  const distanceKm = haversineKm(machineryLatitude, machineryLongitude, deliveryLatitude, deliveryLongitude);
  const legs = returnPickupRequested ? 2 : 1;
  const total = distanceKm * 2 * legs * pricePerKmDelivery + (fixedDispatchFee ?? 0) * legs;
  return { distanceKm, pricePerKm: pricePerKmDelivery, fixedDispatchFee, legs, total };
};

const EARTH_RADIUS_KM = 6371;
const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

const haversineKm = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_KM * c;
};
